/**
 * ProfessionalProfileForm
 * Form for creating/editing professional profile
 */

import { useProfessions } from '@features/professions/hooks'
import { zodResolver } from '@hookform/resolvers/zod'
import { InputWithIcon, SelectWithIcon } from '@shared/components/form'
import { CitySelector } from '@features/search/components'
import { PhoneInput } from '@shared/components/forms'
import { Button } from '@shared/components/ui/button'
import { Label } from '@shared/components/ui/label'
import { Textarea } from '@shared/components/ui/textarea'
import { useBotContact, openWhatsAppChat } from '@shared/lib/api/hooks/useValidation'

import { sanitizeFacebookInput, sanitizeInstagramInput } from '@shared/lib/utils'
import type { Professional, User } from '@shared/types'
import { useProfessionalProfileFormTour } from '@shared/hooks'
import { useLoading } from '@shared/contexts'
import {
  Briefcase,
  ExternalLink,
  Facebook,
  FileText,
  IdCard,
  Instagram,
  Loader2,
  Map,
  MapPin,
} from 'lucide-react'

import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import {
  useActivateProfessionalProfile,
  useCreateProfessionalProfile,
  useDeactivateProfessionalProfile,
  useUpdateProfessionalProfile,
  useUploadProfessionalProfilePicture,
  useRemoveProfessionalProfilePicture,
} from '../hooks/useProfessionalProfile'
import { professionalProfileSchema, type ProfessionalProfileFormData } from '../schemas/profileSchemas'
import { ProfessionalProfileActivationSection } from './ProfessionalProfileActivationSection'
import { ProfessionalProfilePictureSection } from './ProfessionalProfilePictureSection'
import { logger } from '@shared/lib/utils/logger'

// ============================================================================
// Types
// ============================================================================

interface ProfessionalProfileFormProps {
  professional?: Professional
  user?: User
  onSuccess?: () => void
  onCancel?: () => void
  activeProfile: string | undefined
}

const LICENSE_VERIFICATION_MESSAGE = 'verificar matrícula'

// ============================================================================
// Component
// ============================================================================

export function ProfessionalProfileForm({
  professional,
  user,
  onSuccess,
  onCancel,
  activeProfile
}: ProfessionalProfileFormProps) {
  const navigate = useNavigate()
  const { showLoading, hideLoading } = useLoading()
  const isEditing = !!professional
  const { data: professions, isLoading: isLoadingProfessions } = useProfessions()
  const {
    data: botContact,
    isLoading: isLoadingBotContact,
    isError: botContactError,
  } = useBotContact()
  const createProfile = useCreateProfessionalProfile()
  const updateProfile = useUpdateProfessionalProfile()
  const uploadPicture = useUploadProfessionalProfilePicture()
  const removePicture = useRemoveProfessionalProfilePicture()
  const activateProfile = useActivateProfessionalProfile()
  const deactivateProfile = useDeactivateProfessionalProfile()
  const [isUploadingPicture, setIsUploadingPicture] = useState(false)
  const [selectedPictureFile, setSelectedPictureFile] = useState<File | null>(null)
  const [pictureRemoved, setPictureRemoved] = useState(false)
  const [isActivating, setIsActivating] = useState(false)

  // Check if professional account is active
  const isActive = professional?.isActive ?? professional?.activeAccount ?? false

  // Auto-start tour for first-time visitors
  useProfessionalProfileFormTour(activeProfile === 'professional')

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
  } = useForm<ProfessionalProfileFormData>({
    resolver: zodResolver(professionalProfileSchema),
    defaultValues: {
      professionalName: professional?.professionalName || '',
      bio: professional?.bio || '',
      professionId: professional?.professionId || '',
      cityId: professional?.cityId || '',
      gender: professional?.gender || undefined,
      contactPhone: professional?.contactPhone || user?.phone || '',
      profilePictureUrl: professional?.profilePictureUrl || '',
      instagramUrl: professional?.instagramUrl || '',
      facebookUrl: professional?.facebookUrl || '',
      googleMapsUrl: professional?.googleMapsUrl || '',
    },
  })

  const professionId = watch('professionId')
  const cityId = watch('cityId')
  const gender = watch('gender')
  const contactPhone = watch('contactPhone')

  // Construct profile picture URL from professional ID if they have a picture
  const profilePictureUrl = professional?.id && professional?.profilePictureUrl
    ? `${import.meta.env.VITE_API_BASE_URL}/api/professionals/professionalPicture/${professional.id}`
    : undefined

  // Update form when professional profile data loads
  useEffect(() => {
    if (professional) {
      setValue('professionalName', professional.professionalName)
      setValue('bio', professional.bio || '')
      setValue('professionId', professional.professionId)
      setValue('cityId', professional.cityId)
      if (professional.gender) {
        setValue('gender', professional.gender)
      }
      setValue('contactPhone', professional.contactPhone || user?.phone || '')
      setValue('profilePictureUrl', professional.profilePictureUrl || '')
      setValue('instagramUrl', professional.instagramUrl || '')
      setValue('facebookUrl', professional.facebookUrl || '')
      setValue('googleMapsUrl', professional.googleMapsUrl || '')
    }
  }, [professional, user?.phone, setValue])

  // Handle profile picture selection (store file for later upload)
  const handleImageUpload = (file: File | null) => {
    setSelectedPictureFile(file)
    // If file is null and professional had a picture, mark for removal
    if (file === null && professional?.profilePictureUrl) {
      setPictureRemoved(true)
    } else {
      setPictureRemoved(false)
    }
  }

  // Handle professional account activation
  const handleActivate = async () => {
    try {
      setIsActivating(true)
      showLoading('Activando cuenta profesional...')
      await activateProfile.mutateAsync()
      toast.success('Cuenta profesional activada correctamente')
      // Don't hide loading here - let onSuccess handle it to keep overlay visible during redirect
      onSuccess?.()
    } catch (error) {
      logger.error('Failed to activate:', error)
      toast.error('Error al activar la cuenta profesional')
      setIsActivating(false)
      hideLoading()
    }
  }

  // Handle professional account deactivation
  const handleDeactivate = async () => {
    try {
      await deactivateProfile.mutateAsync()
      toast.success('Cuenta profesional desactivada')
      onSuccess?.()
    } catch (error) {
      logger.error('Failed to deactivate:', error)
      toast.error('Error al desactivar la cuenta profesional')
    }
  }

  // Sanitize Instagram input
  const handleInstagramBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const sanitized: string = sanitizeInstagramInput(e.target.value)
    if (sanitized !== e.target.value) {
      setValue('instagramUrl', sanitized)
    }
  }

  // Sanitize Facebook input
  const handleFacebookBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const sanitized: string = sanitizeFacebookInput(e.target.value)
    if (sanitized !== e.target.value) {
      setValue('facebookUrl', sanitized)
    }
  }

  // Handle form submission
  const onSubmit = async (data: ProfessionalProfileFormData) => {
    try {
      showLoading('Guardando perfil profesional...')
      if (isEditing) {
        await updateProfile.mutateAsync(data)
        toast.success('Perfil profesional actualizado correctamente')
      } else {
        await createProfile.mutateAsync(data)
        toast.success('Perfil profesional creado correctamente')
      }

      // Handle picture removal if flagged
      if (pictureRemoved) {
        try {
          setIsUploadingPicture(true)
          showLoading('Eliminando foto de perfil...')
          await removePicture.mutateAsync()
          toast.success('Foto de perfil eliminada')
          setPictureRemoved(false)
        } catch (error) {
          logger.error('Failed to remove picture:', error)
          toast.error('Error al eliminar la foto de perfil')
        } finally {
          setIsUploadingPicture(false)
        }
      }

      // Upload profile picture if one was selected
      if (selectedPictureFile) {
        try {
          setIsUploadingPicture(true)
          showLoading('Subiendo foto de perfil...')
          await uploadPicture.mutateAsync(selectedPictureFile)
          toast.success('Foto de perfil profesional actualizada')
        } catch (error) {
          logger.error('Failed to upload picture:', error)
          toast.error('Error al subir la foto de perfil')
        } finally {
          setIsUploadingPicture(false)
          setSelectedPictureFile(null)
        }
      }

      // Don't hide loading here - let onSuccess handle it to keep overlay visible during redirect
      onSuccess?.()
    } catch (error) {
      logger.error('Failed to save professional profile:', error)
      toast.error('Error al guardar el perfil profesional. Por favor, intenta de nuevo.')
      hideLoading()
    }
  }

  return (
    <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-6">
      {/* Activation Status - Show for both existing profiles and new accounts */}
      {(isEditing || !professional) && (
        <ProfessionalProfileActivationSection
          isEditing={isEditing}
          isActive={isActive}
          isActivating={isActivating}
          isSubmitting={isSubmitting}
          onActivate={() => void handleActivate()}
          onDeactivate={() => void handleDeactivate()}
        />
      )}

      {/* Profile Picture Upload */}
      <ProfessionalProfilePictureSection
        currentImageUrl={profilePictureUrl}
        onImageChange={handleImageUpload}
        fallbackText={professional?.professionalName?.slice(0, 2).toUpperCase() || 'PRO'}
        isUploading={isUploadingPicture}
        isSubmitting={isSubmitting}
        isActive={isActive}
      />

      {/* Professional Information Section */}
      <div className="space-y-4" data-tour="professional-form-basic">
        <h3 className="text-lg font-semibold">Información Profesional</h3>

        {isEditing && (
          <div className="space-y-2">
            <Button
              type="button"
              variant="outline"
              className="w-full"
              disabled={!botContact || isLoadingBotContact || isSubmitting}
              onClick={() =>
                botContact && openWhatsAppChat(botContact, LICENSE_VERIFICATION_MESSAGE)
              }
            >
              <IdCard className="h-4 w-4 mr-2" />
              Verificar matrícula
            </Button>
            {botContactError && (
              <p className="text-sm text-destructive">
                No pudimos obtener el contacto de WhatsApp. Recargá la página para intentar de nuevo.
              </p>
            )}
          </div>
        )}

        {/* Professional Name */}
        <div>
          <InputWithIcon
            type="text"
            placeholder="Nombre profesional"
            {...register('professionalName')}
            disabled={isSubmitting || !isActive}
          />
          {errors.professionalName && (
            <p className="text-sm text-destructive mt-1">{errors.professionalName.message}</p>
          )}
        </div>

        {/* Bio */}
        <div className="space-y-2">
          <Label htmlFor="bio">
            <FileText className="h-4 w-4 inline mr-2" />
            Descripción
          </Label>
          <Textarea
            id="bio"
            {...register('bio')}
            placeholder="Cuéntanos sobre tu experiencia y servicios..."
            rows={4}
            disabled={isSubmitting || !isActive}
          />
          {errors.bio && (
            <p className="text-sm text-destructive">{errors.bio.message}</p>
          )}
          <p className="text-xs text-muted-foreground">
            Máximo 500 caracteres
          </p>
        </div>

        {/* Profession */}
        <div>
          <SelectWithIcon
            icon={Briefcase}
            placeholder="Profesión"
            value={professionId}
            onValueChange={(value: string) => setValue('professionId', value)}
            options={professions?.map(p => ({ value: p._id, label: p.name })) || []}
            disabled={isLoadingProfessions || isSubmitting || !isActive}
          />
          {errors.professionId && (
            <p className="text-sm text-destructive mt-1">{errors.professionId.message}</p>
          )}
        </div>

        {/* City */}
        <div>
          <CitySelector
            placeholder="Ciudad"
            value={cityId}
            onChange={(value: string) => setValue('cityId', value)}
            disabled={isSubmitting || !isActive}
            rightIcon={MapPin}
          />
          {errors.cityId && (
            <p className="text-sm text-destructive mt-1">{errors.cityId.message}</p>
          )}
        </div>

        {/* Gender */}
        <div>
          <SelectWithIcon
            placeholder="Género"
            value={gender || ''}
            onValueChange={(value: string) => setValue('gender', value as 'male' | 'female', { shouldValidate: true })}
            options={[
              { value: 'male', label: 'Masculino' },
              { value: 'female', label: 'Femenino' },
            ]}
            disabled={isSubmitting || !isActive}
          />
          {errors.gender && (
            <p className="text-sm text-destructive mt-1">{errors.gender.message}</p>
          )}
        </div>

        {/* Contact Phone */}
        <div data-tour="professional-form-contact">
          <Label htmlFor="contactPhone" className="mb-2 block text-sm font-medium">
            A dónde te contactarán los clientes
          </Label>
          <PhoneInput
            value={contactPhone}
            onChange={(value) => setValue('contactPhone', value)}
            disabled={isSubmitting || !isActive}
          />
          {errors.contactPhone && (
            <p className="text-sm text-destructive mt-1">{errors.contactPhone.message}</p>
          )}
        </div>
      </div>

      {/* Social Media Section */}
      <div className="space-y-4" data-tour="professional-form-social">
        <h3 className="text-lg font-semibold">Redes Sociales (Opcional)</h3>

        {/* Instagram */}
        <div>
          <InputWithIcon
            icon={Instagram}
            type="text"
            placeholder="Ej.: @professionals.app"
            {...register('instagramUrl')}
            onBlur={handleInstagramBlur}
            disabled={isSubmitting || !isActive}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Solo tu nombre de usuario, sin @ ni URL completa
          </p>
          {errors.instagramUrl && (
            <p className="text-sm text-destructive mt-1">{errors.instagramUrl.message}</p>
          )}
        </div>

        {/* Facebook */}
        <div>
          <InputWithIcon
            icon={Facebook}
            type="text"
            placeholder="Ej.: facebook.com/Professionals.app"
            {...register('facebookUrl')}
            onBlur={handleFacebookBlur}
            disabled={isSubmitting || !isActive}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Tu nombre de usuario, con o sin "facebook.com/"
          </p>
          {errors.facebookUrl && (
            <p className="text-sm text-destructive mt-1">{errors.facebookUrl.message}</p>
          )}
        </div>

        {/* Google Maps */}
        <div>
          <InputWithIcon
            icon={Map}
            type="text"
            placeholder="La dirección de tu local en google maps"
            {...register('googleMapsUrl')}
            disabled={isSubmitting || !isActive}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Dirección completa o URL de Google Maps
          </p>
          {errors.googleMapsUrl && (
            <p className="text-sm text-destructive mt-1">{errors.googleMapsUrl.message}</p>
          )}
        </div>
      </div>

      {/* View Public Profile Button - Only show if profile exists */}
      {isEditing && professional?.id && (
        <div className="border-t pt-4 mb-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => void navigate(`/professional/${professional.id}`)}
            disabled={!isActive}
            className="w-full"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Ver Perfil Público
          </Button>
        </div>
      )}

      {/* Form Actions */}
      <div className="flex gap-4 pt-4">
        <Button type="submit" disabled={isSubmitting || !isActive} className="flex-1">
          {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {isSubmitting
            ? 'Guardando...'
            : isEditing
              ? 'Actualizar Perfil'
              : 'Crear Perfil Profesional'}
        </Button>
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting || !isActive}
          >
            Cancelar
          </Button>
        )}
      </div>

      {/* Success/Error Messages */}
      {(createProfile.isSuccess || updateProfile.isSuccess) && (
        <p className="text-sm text-green-600">
          ¡Perfil profesional {isEditing ? 'actualizado' : 'creado'} correctamente!
        </p>
      )}
      {(createProfile.isError || updateProfile.isError) && (
        <p className="text-sm text-destructive">
          Error al guardar el perfil profesional. Por favor, intenta de nuevo.
        </p>
      )}
    </form>
  )
}
